import {
  Context,
  ComparisonFilter,
  getWidgetData,
  ListFilter,
  LogicalFilter,
  SearchWidgetItem,
  WidgetRequestData,
} from '@sitecore-cloudsdk/search/server';
import { ensureCloudSdkServerForSearch } from 'lib/search/ensure-cloud-sdk-server';

export type RelatedExpertCommentsNewsItem = {
  author_name: string;
  author_title: string;
  date: string;
  description: string;
  id: string;
  image_url: string;
  item_id: string;
  key_contact_id: string;
  name: string;
  news_type: string;
  page_description: string;
  source_id: string;
  subtitle: string;
  type: string;
  url: string;
};

export type FetchRelatedExpertCommentsParams = {
  locale: string;
  authorId?: string;
  currentItemId?: string;
  rfkId?: string;
  defaultItemsPerPage?: number;
  sitecoreEdgeContextId: string;
  siteName: string;
};

export async function fetchRelatedExpertComments(
  params: FetchRelatedExpertCommentsParams
): Promise<{ news: RelatedExpertCommentsNewsItem[]; error: string | null }> {
  const {
    locale,
    authorId,
    currentItemId,
    rfkId = 'rfkid_reports_insights',
    defaultItemsPerPage = 4,
    sitecoreEdgeContextId,
    siteName,
  } = params;

  if (!authorId) {
    return { news: [], error: null };
  }

  if (!sitecoreEdgeContextId?.trim() || !siteName?.trim()) {
    return { news: [], error: null };
  }

  try {
    await ensureCloudSdkServerForSearch({ sitecoreEdgeContextId, siteName });
  } catch {
    return { news: [], error: 'Failed to load news' };
  }

  const searchSourceIds = process.env.NEXT_PUBLIC_SEARCH_NEWS_SOURCE_ID;

  try {
    const [language, countryRaw] = locale.split('-');
    const country = (countryRaw || 'US').toLowerCase();
    const ctx = new Context({
      locale: {
        language: (language || 'en').toLowerCase(),
        country,
      },
    });

    const contactIdFilter = new ListFilter('authors', 'anyOf', [authorId]);
    const typeFilter = new ComparisonFilter('type', 'eq', 'Expert Comment');

    const widgetItem = new SearchWidgetItem(
      'newsarticle',
      rfkId,
      {
        limit: defaultItemsPerPage || 4,
        content: {},
        filter: new LogicalFilter('and', [contactIdFilter, typeFilter]),
      },
      searchSourceIds ? [searchSourceIds] : undefined
    );

    const response = await getWidgetData(new WidgetRequestData([widgetItem]), ctx);
    const content = response?.widgets?.[0]?.content as RelatedExpertCommentsNewsItem[] | undefined;
    const filteredContent = content
      ?.filter((n) => n?.item_id !== currentItemId?.replace(/-/g, ''))
      .slice(0, 3);

    return { news: filteredContent ?? [], error: null };
  } catch {
    return { news: [], error: 'Failed to load news' };
  }
}
